import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';
import { formatDistanceToNow } from 'date-fns';

interface ApiErrorLog {
  id: string;
  service_name: string;
  endpoint: string;
  method: string;
  status_code: number;
  created_at: string;
  user_id: string | null;
  team_id: string | null;
  north_id: string | null;
  metadata: any;
  tokens_used?: number;
  cost_usd?: number;
  error_message?: string;
  error_details?: any;
}

interface InternalApiErrorLog {
  id: string;
  endpoint: string;
  method: string;
  status_code: number;
  response_time_ms: number | null;
  created_at: string;
  team_id: string | null;
  api_key_id: string | null;
}

interface ApiErrorLogViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApiErrorLogViewer({ open, onOpenChange }: ApiErrorLogViewerProps) {
  const [loading, setLoading] = useState(true);
  const [externalErrors, setExternalErrors] = useState<ApiErrorLog[]>([]);
  const [internalErrors, setInternalErrors] = useState<InternalApiErrorLog[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      loadErrors();
    }
  }, [open]);

  const loadErrors = async () => {
    setLoading(true);
    try {
      // Load external API errors (api_call_logs)
      const { data: externalData, error: externalError } = await supabase
        .from('api_call_logs')
        .select('*')
        .gte('status_code', 400)
        .order('created_at', { ascending: false })
        .limit(50);

      if (externalError) {
        logger.error('Error loading external API errors:', externalError);
      } else {
        setExternalErrors((externalData || []) as ApiErrorLog[]);
      }

      // Load internal API errors (api_request_logs)
      try {
        // @ts-ignore - Table exists but types not yet regenerated
        const { data: internalData, error: internalError } = await supabase
          .from('api_request_logs')
          .select('*')
          .gte('status_code', 400)
          .order('created_at', { ascending: false })
          .limit(50);

        if (internalError) {
          logger.error('Error loading internal API errors:', internalError);
        } else {
          setInternalErrors((internalData || []) as unknown as InternalApiErrorLog[]);
        }
      } catch (err) {
        logger.log('Internal API logs table not available yet');
      }
    } catch (error) {
      logger.error('Error loading API errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusBadgeVariant = (statusCode: number): "destructive" | "default" | "secondary" => {
    if (statusCode >= 500) return "destructive";
    if (statusCode >= 400) return "secondary";
    return "default";
  };

  const getStatusLabel = (statusCode: number): string => {
    if (statusCode === 400) return "Bad Request";
    if (statusCode === 401) return "Unauthorized";
    if (statusCode === 403) return "Forbidden";
    if (statusCode === 404) return "Not Found";
    if (statusCode === 429) return "Rate Limited";
    if (statusCode === 500) return "Server Error";
    if (statusCode === 502) return "Bad Gateway";
    if (statusCode === 503) return "Service Unavailable";
    return `${statusCode}`;
  };

  const totalErrors = externalErrors.length + internalErrors.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>API Error Logs</DialogTitle>
              <DialogDescription>
                Recent API errors (4xx/5xx responses) - Last 50 errors
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadErrors} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : totalErrors === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No errors found. Your APIs are running smoothly!
            </div>
          ) : (
            <div className="space-y-6 pr-2">
              {/* External API Errors */}
              {externalErrors.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    External API Errors ({externalErrors.length})
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Errors from third-party services (OpenAI, Zillow, etc.)
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Endpoint</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Cost</TableHead>
                          <TableHead>Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {externalErrors.map((error) => (
                          <>
                            <TableRow key={error.id} className="cursor-pointer hover:bg-muted/50">
                              <TableCell className="text-xs">
                                {formatDistanceToNow(new Date(error.created_at), { addSuffix: true })}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{error.service_name}</Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-[200px] truncate">
                                {error.endpoint}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{error.method}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={getStatusBadgeVariant(error.status_code)}>
                                  {getStatusLabel(error.status_code)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">
                                {error.cost_usd ? `$${error.cost_usd.toFixed(4)}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleRowExpansion(error.id)}
                                >
                                  {expandedRows.has(error.id) ? 'Hide' : 'Show'}
                                </Button>
                              </TableCell>
                            </TableRow>
                            {expandedRows.has(error.id) && (
                              <TableRow>
                                <TableCell colSpan={7} className="bg-muted/30">
                                  <div className="p-4 space-y-3 text-sm">
                                    {error.error_message && (
                                      <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                                        <span className="font-semibold text-red-700 dark:text-red-400">Error Message:</span>
                                        <p className="mt-1 text-red-900 dark:text-red-200">{error.error_message}</p>
                                      </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <span className="font-semibold">Error ID:</span>{' '}
                                        <span className="font-mono text-xs">{error.id}</span>
                                      </div>
                                      <div>
                                        <span className="font-semibold">Timestamp:</span>{' '}
                                        {new Date(error.created_at).toLocaleString()}
                                      </div>
                                      {error.tokens_used && (
                                        <div>
                                          <span className="font-semibold">Tokens Used:</span>{' '}
                                          {error.tokens_used.toLocaleString()}
                                        </div>
                                      )}
                                      {error.north_id && (
                                        <div>
                                          <span className="font-semibold">North ID:</span>{' '}
                                          <span className="font-mono text-xs">{error.north_id}</span>
                                        </div>
                                      )}
                                      {error.team_id && (
                                        <div>
                                          <span className="font-semibold">Team ID:</span>{' '}
                                          <span className="font-mono text-xs">{error.team_id}</span>
                                        </div>
                                      )}
                                    </div>
                                    {error.error_details && Object.keys(error.error_details).length > 0 && (
                                      <div className="mt-3">
                                        <span className="font-semibold">Error Details:</span>
                                        <pre className="mt-2 p-3 bg-background rounded border text-xs overflow-auto max-h-[200px]">
                                          {JSON.stringify(error.error_details, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                    {error.metadata && Object.keys(error.metadata).length > 0 && (
                                      <div className="mt-3">
                                        <span className="font-semibold">Metadata:</span>
                                        <pre className="mt-2 p-3 bg-background rounded border text-xs overflow-auto max-h-[200px]">
                                          {JSON.stringify(error.metadata, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Internal API Errors */}
              {internalErrors.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Internal API Errors ({internalErrors.length})
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Errors from North Reports API endpoints
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Endpoint</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Response Time</TableHead>
                          <TableHead>Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {internalErrors.map((error) => (
                          <>
                            <TableRow key={error.id} className="cursor-pointer hover:bg-muted/50">
                              <TableCell className="text-xs">
                                {formatDistanceToNow(new Date(error.created_at), { addSuffix: true })}
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-[200px] truncate">
                                {error.endpoint}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{error.method}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={getStatusBadgeVariant(error.status_code)}>
                                  {getStatusLabel(error.status_code)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">
                                {error.response_time_ms ? `${error.response_time_ms}ms` : '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleRowExpansion(`internal-${error.id}`)}
                                >
                                  {expandedRows.has(`internal-${error.id}`) ? 'Hide' : 'Show'}
                                </Button>
                              </TableCell>
                            </TableRow>
                            {expandedRows.has(`internal-${error.id}`) && (
                              <TableRow>
                                <TableCell colSpan={6} className="bg-muted/30">
                                  <div className="p-4 space-y-2 text-sm">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <span className="font-semibold">Error ID:</span>{' '}
                                        <span className="font-mono text-xs">{error.id}</span>
                                      </div>
                                      <div>
                                        <span className="font-semibold">Timestamp:</span>{' '}
                                        {new Date(error.created_at).toLocaleString()}
                                      </div>
                                      {error.team_id && (
                                        <div>
                                          <span className="font-semibold">Team ID:</span>{' '}
                                          <span className="font-mono text-xs">{error.team_id}</span>
                                        </div>
                                      )}
                                      {error.api_key_id && (
                                        <div>
                                          <span className="font-semibold">API Key ID:</span>{' '}
                                          <span className="font-mono text-xs">{error.api_key_id}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
